import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { WRITE_ROLES } from '@familienbaum/shared';
import { useQueryClient } from '@tanstack/react-query';
import { keys, useAuthStatus, useSession } from '@/lib/queries';
import { UNAUTHORIZED_EVENT } from '@/lib/api';
import { AppShell } from '@/components/layout/app-shell';
import { BrandMark } from '@/components/layout/brand-mark';
import { Spinner } from '@/components/ui/misc';

const LoginPage = lazy(() => import('@/routes/login'));
const SetupPage = lazy(() => import('@/routes/setup'));
const InvitePage = lazy(() => import('@/routes/invite'));
const DashboardPage = lazy(() => import('@/routes/dashboard'));
const PersonsPage = lazy(() => import('@/routes/persons'));
const PersonDetailPage = lazy(() => import('@/routes/person-detail'));
const PersonFormPage = lazy(() => import('@/routes/person-form'));
const TreePage = lazy(() => import('@/routes/tree'));
const RelationshipsPage = lazy(() => import('@/routes/relationships'));
const EventsPage = lazy(() => import('@/routes/events'));
const PlacesPage = lazy(() => import('@/routes/places'));
const MediaPage = lazy(() => import('@/routes/media'));
const MediaDetailPage = lazy(() => import('@/routes/media-detail'));
const SourcesPage = lazy(() => import('@/routes/sources'));
const QuestionsPage = lazy(() => import('@/routes/questions'));
const GapsPage = lazy(() => import('@/routes/gaps'));
const ActivityPage = lazy(() => import('@/routes/activity'));
const UsersPage = lazy(() => import('@/routes/users'));
const ImportPage = lazy(() => import('@/routes/import'));
const ImportDetailPage = lazy(() => import('@/routes/import-detail'));
const SettingsPage = lazy(() => import('@/routes/settings'));
const NotFoundPage = lazy(() => import('@/routes/not-found'));

function Splash({ label = 'Wird geladen…' }: { label?: string }) {
  return (
    <div className="flex h-full min-h-64 flex-col items-center justify-center gap-3">
      <BrandMark className="size-12" />
      <span className="flex items-center gap-2 text-[13px] text-ink-3">
        <Spinner />
        {label}
      </span>
    </div>
  );
}

/** Statt des Formulars die Personenseite – dort steht dasselbe zum Lesen. */
function PersonDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/personen/${id}` : '/personen'} replace />;
}

/** Nach einem 401 aus einem beliebigen Request zur Anmeldung schicken. */
function useUnauthorizedRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    const handler = () => {
      queryClient.setQueryData(keys.session, null);
      const isPublic = ['/anmelden', '/einrichten', '/einladung'].some((path) =>
        location.pathname.startsWith(path),
      );
      if (!isPublic) navigate('/anmelden', { replace: true, state: { from: location.pathname } });
    };
    window.addEventListener(UNAUTHORIZED_EVENT, handler);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handler);
  }, [navigate, location.pathname, queryClient]);
}

export function AppRoutes() {
  useUnauthorizedRedirect();

  const session = useSession();
  const status = useAuthStatus();

  if (session.isLoading || status.isLoading) {
    return <Splash />;
  }

  const user = session.data ?? null;
  const needsSetup = status.data?.needsSetup ?? false;
  /**
   * Leser sehen keine Formulare – auch nicht, wer die Adresse kennt oder ein
   * altes Lesezeichen öffnet. Der Server lehnt das Speichern ohnehin ab; ein
   * ausgefülltes Formular, das erst beim Speichern scheitert, wäre die
   * unfreundlichste Art, das mitzuteilen.
   */
  const canWrite = Boolean(user && WRITE_ROLES.includes(user.role));

  return (
    <Suspense fallback={<Splash />}>
      <Routes>
        <Route
          path="/einrichten"
          element={needsSetup ? <SetupPage /> : <Navigate to="/" replace />}
        />
        <Route
          path="/anmelden"
          element={
            user ? <Navigate to="/" replace /> : needsSetup ? <Navigate to="/einrichten" replace /> : <LoginPage />
          }
        />
        <Route path="/einladung/:token" element={<InvitePage />} />

        {user ? (
          <Route element={<AppShell user={user} />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/personen" element={<PersonsPage />} />
            <Route
              path="/personen/neu"
              element={canWrite ? <PersonFormPage mode="create" /> : <Navigate to="/personen" replace />}
            />
            <Route path="/personen/:id" element={<PersonDetailPage />} />
            <Route
              path="/personen/:id/bearbeiten"
              element={canWrite ? <PersonFormPage mode="edit" /> : <PersonDetailRedirect />}
            />
            <Route path="/stammbaum" element={<TreePage />} />
            <Route path="/beziehungen" element={<RelationshipsPage />} />
            <Route path="/ereignisse" element={<EventsPage />} />
            <Route path="/orte" element={<PlacesPage />} />
            <Route path="/medien" element={<MediaPage />} />
            <Route path="/medien/:id" element={<MediaDetailPage />} />
            <Route path="/quellen" element={<SourcesPage />} />
            <Route path="/fragen" element={<QuestionsPage />} />
            <Route path="/luecken" element={<GapsPage />} />
            {/* Leser haben nichts geändert – für sie gibt es kein Protokoll. */}
            <Route
              path="/aktivitaeten"
              element={canWrite ? <ActivityPage /> : <Navigate to="/" replace />}
            />
            <Route path="/benutzer" element={user.role === 'admin' ? <UsersPage /> : <Navigate to="/" replace />} />
            {/*
              Der Import legt in einem Zug hunderte Personen an und entscheidet
              dabei über `isLiving` mit, wer im Archiv wen sieht. Deshalb
              Administratoren – dieselbe Grenze wie bei Benutzern und Zweigen.
            */}
            <Route path="/import" element={user.role === 'admin' ? <ImportPage /> : <Navigate to="/" replace />} />
            <Route path="/import/:id" element={user.role === 'admin' ? <ImportDetailPage /> : <Navigate to="/" replace />} />
            <Route path="/einstellungen" element={<SettingsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        ) : (
          <Route
            path="*"
            element={<Navigate to={needsSetup ? '/einrichten' : '/anmelden'} replace />}
          />
        )}
      </Routes>
    </Suspense>
  );
}
